import { useState, useRef, useEffect } from 'react';
import { LogOut, Menu } from 'lucide-react';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/authService';
import { WorkspaceSelector } from '../common/WorkspaceSelector';
import { CreditIndicator } from '../CreditIndicator';
import { cn } from '../../utils/cn';

export default function Header() {
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    // Hit the server logout API to blacklist the token, then clear local state
    await authService.logout();
    logout();
  };

  const initials = user?.email
    ? user.email.charAt(0).toUpperCase()
    : 'U';

  return (
    <header className="flex items-center justify-between px-4 h-14 bg-white border-b border-neutral-200">
      {/* Left: logo + open sidebar button (only when sidebar is closed) */}
      <div className="flex items-center gap-2">
        {!sidebarOpen && (
          <button
            type="button"
            className="p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
            onClick={() => useUIStore.getState().setSidebarOpen(true)}
            aria-label="Show sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}

        <img
          src="/images/Oquis.ai-H-logo-color.png"
          alt="Oquis Logo"
          className="h-7 w-auto"
        />
      </div>

      {/* Center: workspace selector */}
      <div className="hidden sm:flex items-center">
        <WorkspaceSelector />
      </div>

      {/* Right: credits + user menu */}
      <div className="flex items-center gap-3">
        <CreditIndicator compact />

        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors',
              'bg-primary-100 text-primary-700 hover:bg-primary-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
            )}
            aria-label="User menu"
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            {initials}
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 z-50 w-56 bg-white border border-neutral-200 rounded-lg shadow-lg py-1">
              <div className="px-3 py-2 border-b border-neutral-100">
                <p className="text-sm font-medium text-neutral-900 truncate">
                  {user?.email || 'User'}
                </p>
              </div>

              <div className="sm:hidden px-3 py-2 border-b border-neutral-100">
                <WorkspaceSelector />
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
